import { useState, useEffect } from 'react';
import { useUI } from '../context/UIContext';
import { useTheme } from '../context/ThemeContext';
import locationService from '../lib/locationService';

// Icons
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const UsersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const LocationFilter = ({ isOpen, onClose, onApplyFilter, currentFilter, userProfile }) => {
  const { setHideNav } = useUI();
  const { theme, isDark } = useTheme();
  const [filterType, setFilterType] = useState('none'); // 'none', 'following', 'location'
  const [locationMode, setLocationMode] = useState('nearby'); // 'nearby', 'city'
  const [radius, setRadius] = useState(50);
  const [cityQuery, setCityQuery] = useState('');
  const [cityCoords, setCityCoords] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [userLocationName, setUserLocationName] = useState('');
  const [cityLoading, setCityLoading] = useState(false);
  const [cityError, setCityError] = useState(null);

  // Hide nav when filter is open
  useEffect(() => {
    setHideNav(isOpen);
  }, [isOpen, setHideNav]);

  // Initialize from user profile location or current filter when opened
  useEffect(() => {
    if (isOpen) {
      // Restore from current filter if exists
      if (currentFilter) {
        if (currentFilter.mode === 'following') {
          setFilterType('following');
        } else if (currentFilter.mode === 'nearby' || currentFilter.mode === 'city') {
          setFilterType('location');
          setLocationMode(currentFilter.mode);
          setRadius(currentFilter.radius || 50);
          if (currentFilter.mode === 'nearby' && currentFilter.userLocation) {
            setUserLocation(currentFilter.userLocation);
            setUserLocationName(currentFilter.locationName || '');
          } else if (currentFilter.mode === 'city') {
            setCityQuery(currentFilter.cityName || '');
            setCityCoords(currentFilter.cityCoords);
          }
        }
      } else {
        setFilterType('none');
      }

      // Auto-populate user location if available
      if (userProfile?.latitude && userProfile?.longitude) {
        setUserLocation({
          latitude: userProfile.latitude,
          longitude: userProfile.longitude,
        });
        const locationParts = [];
        if (userProfile.city) locationParts.push(userProfile.city);
        if (userProfile.state) locationParts.push(userProfile.state);
        setUserLocationName(locationParts.join(', ') || 'Your location');
      }
    }
  }, [isOpen, currentFilter, userProfile]);

  // Search for a city/zip
  const handleCitySearch = async () => {
    if (!cityQuery.trim()) return;

    setCityLoading(true);
    setCityError(null);

    try {
      const result = await locationService.geocodeAddress(cityQuery);
      if (result.success) {
        setCityCoords({
          latitude: result.coords.latitude,
          longitude: result.coords.longitude,
        });
        setCityQuery(result.displayName?.split(',')[0] || cityQuery);
        setLocationMode('city');
      } else {
        setCityError('Location not found');
      }
    } catch (err) {
      setCityError('Failed to search location');
    } finally {
      setCityLoading(false);
    }
  };

  // Apply the filter
  const handleApply = () => {
    if (filterType === 'none') {
      onApplyFilter(null);
    } else if (filterType === 'following') {
      onApplyFilter({
        mode: 'following',
      });
    } else if (filterType === 'location') {
      if (locationMode === 'nearby' && userLocation) {
        onApplyFilter({
          mode: 'nearby',
          userLocation,
          locationName: userLocationName,
          radius,
          cityName: null,
          cityCoords: null,
        });
      } else if (locationMode === 'city' && cityCoords) {
        onApplyFilter({
          mode: 'city',
          userLocation: null,
          radius,
          cityName: cityQuery,
          cityCoords,
        });
      }
    }
    onClose();
  };

  // Clear filter
  const handleClear = () => {
    setFilterType('none');
    setLocationMode('nearby');
    setRadius(50);
    setCityQuery('');
    setCityCoords(null);
    onApplyFilter(null);
    onClose();
  };

  if (!isOpen) return null;

  const hasUserLocation = userLocation !== null;
  const canApply = filterType === 'none' ||
    filterType === 'following' ||
    (filterType === 'location' && ((locationMode === 'nearby' && hasUserLocation) || (locationMode === 'city' && cityCoords)));

  const styleSheet = styles(theme, isDark);

  return (
    <>
      {/* Backdrop */}
      <div style={styleSheet.backdrop} onClick={onClose} />

      {/* Top Sheet */}
      <div style={styleSheet.sheet}>
        {/* Header */}
        <div style={styleSheet.header}>
          <h2 style={styleSheet.title}>Filter Posts</h2>
          <button style={styleSheet.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div style={styleSheet.content}>
          {/* Filter Type Selection - Radio Style */}
          <div style={styleSheet.filterTypeSection}>
            {/* None Option */}
            <button
              style={{
                ...styleSheet.radioOption,
                ...(filterType === 'none' ? styleSheet.radioOptionActive : {}),
              }}
              onClick={() => setFilterType('none')}
            >
              <div style={{
                ...styleSheet.radioCircle,
                ...(filterType === 'none' ? styleSheet.radioCircleActive : {}),
              }}>
                {filterType === 'none' && <div style={styleSheet.radioDot} />}
              </div>
              <span style={styleSheet.radioLabel}>Show All Posts</span>
            </button>

            {/* Following Option */}
            <button
              style={{
                ...styleSheet.radioOption,
                ...(filterType === 'following' ? styleSheet.radioOptionActive : {}),
              }}
              onClick={() => setFilterType('following')}
            >
              <div style={{
                ...styleSheet.radioCircle,
                ...(filterType === 'following' ? styleSheet.radioCircleActive : {}),
              }}>
                {filterType === 'following' && <div style={styleSheet.radioDot} />}
              </div>
              <div style={styleSheet.radioContent}>
                <div style={styleSheet.radioLabelRow}>
                  <UsersIcon />
                  <span style={styleSheet.radioLabel}>Traders I Follow</span>
                </div>
                <span style={styleSheet.radioDesc}>Only show posts from people you follow</span>
              </div>
            </button>

            {/* Location Option */}
            <button
              style={{
                ...styleSheet.radioOption,
                ...(filterType === 'location' ? styleSheet.radioOptionActive : {}),
              }}
              onClick={() => setFilterType('location')}
            >
              <div style={{
                ...styleSheet.radioCircle,
                ...(filterType === 'location' ? styleSheet.radioCircleActive : {}),
              }}>
                {filterType === 'location' && <div style={styleSheet.radioDot} />}
              </div>
              <div style={styleSheet.radioContent}>
                <div style={styleSheet.radioLabelRow}>
                  <MapPinIcon />
                  <span style={styleSheet.radioLabel}>By Location</span>
                </div>
                <span style={styleSheet.radioDesc}>Filter by distance from a location</span>
              </div>
            </button>
          </div>

          {/* Location Options - Only show when location filter is selected */}
          {filterType === 'location' && (
            <div style={styleSheet.locationSection}>
              {/* My Location Option */}
              {hasUserLocation && (
                <button
                  style={{
                    ...styleSheet.locationOption,
                    ...(locationMode === 'nearby' ? styleSheet.locationOptionActive : {}),
                  }}
                  onClick={() => setLocationMode('nearby')}
                >
                  <div style={styleSheet.locationOptionLeft}>
                    <div style={{
                      ...styleSheet.locationIcon,
                      ...(locationMode === 'nearby' ? styleSheet.locationIconActive : {}),
                    }}>
                      <MapPinIcon />
                    </div>
                    <div style={styleSheet.locationText}>
                      <span style={styleSheet.locationTitle}>My Location</span>
                      <span style={styleSheet.locationSubtitle}>{userLocationName}</span>
                    </div>
                  </div>
                  {locationMode === 'nearby' && (
                    <div style={styleSheet.checkIcon}>
                      <CheckIcon />
                    </div>
                  )}
                </button>
              )}

              {/* No location message */}
              {!hasUserLocation && (
                <div style={styleSheet.noLocationMessage}>
                  <MapPinIcon />
                  <span>Set your location in Profile to use "My Location" filter</span>
                </div>
              )}

              {/* Divider */}
              <div style={styleSheet.divider}>
                <div style={styleSheet.dividerLine} />
                <span style={styleSheet.dividerText}>or search a location</span>
                <div style={styleSheet.dividerLine} />
              </div>

              {/* City/Zip Search */}
              <div style={styleSheet.searchSection}>
                <div style={styleSheet.searchRow}>
                  <input
                    type="text"
                    placeholder="Enter city or zip code..."
                    value={cityQuery}
                    onChange={(e) => setCityQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCitySearch()}
                    style={{
                      ...styleSheet.searchInput,
                      ...(locationMode === 'city' && cityCoords ? styleSheet.searchInputActive : {}),
                    }}
                  />
                  <button
                    style={styleSheet.searchButton}
                    onClick={handleCitySearch}
                    disabled={cityLoading || !cityQuery.trim()}
                  >
                    {cityLoading ? '...' : <SearchIcon />}
                  </button>
                </div>
                {cityError && <p style={styleSheet.errorText}>{cityError}</p>}
                {locationMode === 'city' && cityCoords && (
                  <div style={styleSheet.cityConfirm}>
                    <CheckIcon />
                    <span>Searching near {cityQuery}</span>
                  </div>
                )}
              </div>

              {/* Radius Slider */}
              <div style={styleSheet.radiusContainer}>
                <div style={styleSheet.radiusHeader}>
                  <span style={styleSheet.radiusLabel}>Search Radius</span>
                  <span style={styleSheet.radiusValue}>{radius} miles</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="200"
                  step="5"
                  value={radius}
                  onChange={(e) => setRadius(parseInt(e.target.value))}
                  style={styleSheet.slider}
                />
                <div style={styleSheet.radiusMarks}>
                  <span>5 mi</span>
                  <span>100 mi</span>
                  <span>200 mi</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styleSheet.footer}>
          <button style={styleSheet.clearButton} onClick={handleClear}>
            Clear
          </button>
          <button
            style={{
              ...styleSheet.applyButton,
              ...(!canApply ? styleSheet.applyButtonDisabled : {}),
            }}
            onClick={handleApply}
            disabled={!canApply}
          >
            Apply Filter
          </button>
        </div>

        {/* Handle at bottom */}
        <div style={styleSheet.handleContainer}>
          <div style={styleSheet.handle} />
        </div>
      </div>
    </>
  );
};

const styles = (theme, isDark) => ({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
  },
  sheet: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    paddingTop: 'env(safe-area-inset-top, 0px)',
    backgroundColor: theme.bg,
    borderBottomLeftRadius: '20px',
    borderBottomRightRadius: '20px',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '85vh',
    boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.1)',
    animation: 'slideDown 0.3s ease',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.bgCard}`,
  },
  title: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '20px',
  },
  filterTypeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    width: '100%',
    padding: '16px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s ease',
  },
  radioOptionActive: {
    backgroundColor: `rgba(14, 165, 233, ${isDark ? '0.1' : '0.15'})`,
    borderColor: theme.accent,
  },
  radioCircle: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    border: `2px solid ${theme.textSecondary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '2px',
  },
  radioCircleActive: {
    borderColor: theme.accent,
  },
  radioDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: theme.accent,
  },
  radioContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  radioLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.accent,
  },
  radioLabel: {
    color: theme.text,
    fontSize: '15px',
    fontWeight: '600',
  },
  radioDesc: {
    color: theme.textSecondary,
    fontSize: '13px',
    marginLeft: '26px',
  },
  locationSection: {
    paddingTop: '16px',
    borderTop: `1px solid ${theme.border}`,
  },
  locationOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '14px 16px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  locationOptionActive: {
    backgroundColor: `rgba(14, 165, 233, ${isDark ? '0.1' : '0.15'})`,
    borderColor: theme.accent,
  },
  locationOptionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  locationIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: theme.border,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textMuted,
  },
  locationIconActive: {
    backgroundColor: `rgba(14, 165, 233, ${isDark ? '0.2' : '0.25'})`,
    color: theme.accent,
  },
  locationText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
  },
  locationTitle: {
    color: theme.text,
    fontSize: '15px',
    fontWeight: '600',
  },
  locationSubtitle: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  checkIcon: {
    color: theme.accent,
  },
  noLocationMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 16px',
    backgroundColor: theme.bgSheet,
    borderRadius: '12px',
    color: theme.textSecondary,
    fontSize: '13px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '20px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: theme.border,
  },
  dividerText: {
    color: theme.textMuted,
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  searchSection: {
    marginBottom: '16px',
  },
  searchRow: {
    display: 'flex',
    gap: '10px',
  },
  searchInput: {
    flex: 1,
    padding: '14px 16px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    color: theme.text,
    fontSize: '14px',
    outline: 'none',
  },
  searchInputActive: {
    backgroundColor: `rgba(14, 165, 233, ${isDark ? '0.1' : '0.15'})`,
    borderColor: theme.accent,
  },
  searchButton: {
    width: '50px',
    padding: '14px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: theme.error,
    fontSize: '12px',
    margin: '8px 0 0 0',
  },
  cityConfirm: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '10px',
    color: theme.accent,
    fontSize: '13px',
    fontWeight: '500',
  },
  radiusContainer: {
    padding: '16px',
    backgroundColor: theme.bgSheet,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
  },
  radiusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  radiusLabel: {
    color: theme.textMuted,
    fontSize: '13px',
  },
  radiusValue: {
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  slider: {
    width: '100%',
    height: '6px',
    borderRadius: '3px',
    appearance: 'none',
    background: `linear-gradient(to right, ${theme.accent}, ${theme.accent})`,
    cursor: 'pointer',
    WebkitAppearance: 'none',
  },
  radiusMarks: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
    color: theme.textMuted,
    fontSize: '11px',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: `1px solid ${theme.bgCard}`,
  },
  clearButton: {
    flex: 1,
    padding: '14px',
    backgroundColor: 'transparent',
    border: `1px solid ${isDark ? '#3a3a3a' : theme.border}`,
    borderRadius: '12px',
    color: theme.textMuted,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  applyButton: {
    flex: 2,
    padding: '14px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  applyButtonDisabled: {
    backgroundColor: theme.border,
    color: theme.textMuted,
    cursor: 'not-allowed',
  },
  handleContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 0 12px',
  },
  handle: {
    width: '40px',
    height: '4px',
    backgroundColor: isDark ? '#3a3a3a' : theme.border,
    borderRadius: '2px',
  },
});

export default LocationFilter;
